import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, BookOpen, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const field =
  "w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60";
const empty = { title: "", author: "", description: "", price: "", visible: true };

export const BooksManager = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/books").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setPdfFile(null); setCoverFile(null); setOpen(true); };
  const openEdit = (b) => { setForm({ title: b.title, author: b.author || "", description: b.description || "", price: b.price || "", visible: b.visible }); setEditId(b.id); setPdfFile(null); setCoverFile(null); setOpen(true); };

  const save = async () => {
    if (!editId && !pdfFile) { toast.error("Le fichier PDF est requis"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("author", form.author);
      fd.append("description", form.description);
      fd.append("price", form.price);
      fd.append("visible", form.visible);
      if (pdfFile) fd.append("pdf", pdfFile);
      if (coverFile) fd.append("cover", coverFile);
      if (editId) await api.put(`/admin/books/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/admin/books", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editId ? "Livre mis à jour" : "Livre ajouté");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce livre ?")) return;
    await api.delete(`/admin/books/${id}`);
    toast.success("Livre supprimé");
    load();
  };

  const toggleVisible = async (b) => {
    const fd = new FormData();
    fd.append("title", b.title);
    fd.append("author", b.author || "");
    fd.append("description", b.description || "");
    fd.append("price", b.price || "");
    fd.append("visible", !b.visible);
    await api.put(`/admin/books/${b.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    load();
  };

  const downloadPdf = async (b) => {
    try {
      const res = await api.get(`/admin/books/${b.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.pdf_filename || `${b.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  return (
    <div data-testid="admin-books-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-amber-50">Livres (PDF)</h1>
          <p className="text-slate-400">Téléversez vos ouvrages ; ils apparaissent dans la catégorie « Livres ».</p>
        </div>
        <button onClick={openNew} data-testid="admin-add-book-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform">
          <Plus size={18} /> Ajouter un livre
        </button>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((b) => (
          <div key={b.id} className="glass rounded-2xl overflow-hidden" data-testid={`book-row-${b.id}`}>
            <div className="aspect-[3/4] bg-gradient-to-br from-amber-900/20 to-black flex items-center justify-center">
              {b.cover_path ? (
                <img src={`${API}/books/${b.id}/cover`} alt={b.title} className="w-full h-full object-cover" />
              ) : (
                <BookOpen className="text-amber-500/40" size={56} />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="font-serif text-lg text-amber-100 truncate">{b.title}</h3>
                {!b.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 shrink-0">Masqué</span>}
              </div>
              {b.price && <span className="font-mono-accent text-amber-300">{b.price}</span>}
              <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><FileText size={12} /> {b.pdf_filename}</p>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => downloadPdf(b)} data-testid={`book-download-${b.id}`} className="p-2 rounded-lg hover:bg-white/5 text-emerald-300" title="Télécharger le PDF"><Download size={15} /></button>
                <button onClick={() => toggleVisible(b)} className="p-2 rounded-lg hover:bg-white/5 text-slate-400">{b.visible ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                <button onClick={() => openEdit(b)} className="p-2 rounded-lg hover:bg-white/5 text-amber-300"><Pencil size={15} /></button>
                <button onClick={() => remove(b.id)} data-testid={`book-delete-${b.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0D0D12] border-amber-500/20 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-amber-100">{editId ? "Modifier le livre" : "Nouveau livre"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input className={field} placeholder="Titre" data-testid="book-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input className={field} placeholder="Auteur" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            <textarea rows={3} className={field} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input className={field} placeholder="Prix (ex: 19€)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <label className="block text-xs uppercase tracking-widest font-mono-accent text-amber-400/70">Fichier PDF {editId && "(laisser vide pour garder l'actuel)"}</label>
            <input type="file" accept="application/pdf" data-testid="book-form-pdf" onChange={(e) => setPdfFile(e.target.files[0])} className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500/20 file:text-amber-200" />
            <label className="block text-xs uppercase tracking-widest font-mono-accent text-amber-400/70">Couverture (image, optionnel)</label>
            <input type="file" accept="image/*" data-testid="book-form-cover" onChange={(e) => setCoverFile(e.target.files[0])} className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500/20 file:text-amber-200" />
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="accent-amber-500" /> Visible sur le site</label>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving || !form.title} data-testid="book-form-save" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Enregistrer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
